import { NotificationDTO } from '@/api/DTO/notification/notification.interface';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NotificationItem from './NotificationItem';

interface NotificationPanelProps {
	/** Already trimmed to the preview size and sorted newest first. */
	notifications: NotificationDTO[];
	/** Total unread for this staff member, matching the bell badge. */
	unreadCount: number;
	/** Unread notifications older than the preview, so none go silently missing. */
	hiddenUnreadCount: number;
	onNotificationClick: (notification: NotificationDTO) => void;
	onViewAll: () => void;
}

/**
 * Contents of the popover that drops below the bell.
 *
 * A preview of the newest notifications — subject and time only — with a "View all"
 * footer leading to the full table. Nothing paginates here: the popover exists to
 * get the staff member to the right notification in one click, and the summary page
 * is where browsing history belongs.
 */
export default function NotificationPanel({
	notifications,
	unreadCount,
	hiddenUnreadCount,
	onNotificationClick,
	onViewAll,
}: NotificationPanelProps) {
	const { t } = useTranslation();
	const isEmpty = notifications.length === 0;

	return (
		<Box sx={{ width: 400, maxWidth: '100vw' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					px: 2.5,
					py: 1.75,
				}}
			>
				<Typography
					sx={{ fontSize: 16, fontWeight: 700, color: 'common.neutral20' }}
				>
					{t('notification.title')}
				</Typography>
				{unreadCount > 0 && (
					<Typography sx={{ fontSize: 13, color: 'common.neutral50' }}>
						{t('notification.unreadCount', { count: unreadCount })}
					</Typography>
				)}
			</Box>
			<Divider sx={{ borderColor: 'common.neutral90' }} />

			<Box
				sx={{
					maxHeight: 'min(60vh, 480px)',
					overflowY: 'auto',
					// keep the scrollbar unobtrusive against the narrow rows
					'&::-webkit-scrollbar': { width: 6 },
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: 'common.neutral90',
						borderRadius: 3,
					},
				}}
			>
				{isEmpty ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 1.5,
							py: 6,
							px: 3,
						}}
					>
						<NotificationsNoneOutlinedIcon
							sx={{ fontSize: 40, color: 'common.neutral70' }}
						/>
						<Typography sx={{ fontSize: 14, color: 'common.neutral50' }}>
							{t('notification.empty')}
						</Typography>
					</Box>
				) : (
					notifications.map((notification) => (
						<NotificationItem
							key={notification.id}
							notification={notification}
							onClick={onNotificationClick}
						/>
					))
				)}
			</Box>

			<Divider sx={{ borderColor: 'common.neutral90' }} />
			<Box sx={{ px: 1.5, py: 1 }}>
				{/*
					Without this the badge lies: it counts every unread, but the preview
					only reaches the newest few, so a staff member back from leave sees
					"35 unread" above a list showing three of them.
				*/}
				{hiddenUnreadCount > 0 && (
					<Typography
						sx={{
							textAlign: 'center',
							fontSize: 12,
							color: 'common.neutral50',
							pt: 0.5,
						}}
					>
						{t('notification.moreUnread', { count: hiddenUnreadCount })}
					</Typography>
				)}
				<Button
					fullWidth
					endIcon={<ArrowForwardIcon />}
					onClick={onViewAll}
					sx={{ justifyContent: 'center', fontSize: 14 }}
				>
					{t('notification.viewAll')}
				</Button>
			</Box>
		</Box>
	);
}
